// Screen 2 - "Ask your crew" (docs/design/slice3-knowledge-ux.md §5).
// POST /v1/apps/:appId/ask-crew (src/api/knowledge.ts), plus GET .../knowledge-doc reused
// as the knowledge-not-ready heuristic (§5.3, flagged there as unconfirmed, §6.1 item 2).
import { useEffect, useId, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { isErr } from "@nightcrew/shared";
import type { AskCrewAnswer, DetectedLanguage } from "@nightcrew/shared";
import type { AppDetailContext } from "./AppDetail.js";
import { askCrew, getKnowledgeDoc, isThrottled, knowledgeErrorKind } from "../api/knowledge.js";
import { Field } from "../components/Field.js";
import { CrewAnswer } from "../components/CrewAnswer.js";
import glimIllustration from "../assets/glim.svg";

const STALL_TIMEOUT_MS = 30_000;
const SOFT_LIMIT_WARN_AT = 3500;
const HARD_LIMIT = 4000;

const LANGUAGE_CODES: readonly DetectedLanguage[] = [
  "en",
  "nl",
  "fr",
  "de",
  "es",
  "it",
  "pt",
  "pl",
];

const SUGGESTED_QUESTIONS = [
  "Why was it slow yesterday?",
  "Are we GDPR ready?",
  "What did the last fix change?",
  "Is my Stripe setup safe?",
];

type Exchange =
  | { readonly status: "pending"; readonly id: string; readonly question: string }
  | { readonly status: "stalled"; readonly id: string; readonly question: string }
  | { readonly status: "error"; readonly id: string; readonly question: string }
  | { readonly status: "throttled"; readonly id: string; readonly question: string }
  | {
      readonly status: "answered";
      readonly id: string;
      readonly question: string;
      readonly answer: AskCrewAnswer;
    };

/**
 * `"error"` is deliberately absent: an outright probe failure (e.g. `unavailable`)
 * fails OPEN to `"ready"` (the composer stays usable and the real ask-crew call
 * surfaces its own error if something is genuinely broken) rather than blocking the
 * whole screen on a non-blocking readiness hint - a judgment call, flagged in the
 * dispatch return since the ux spec's §5.3 state table does not name this branch.
 */
type Readiness = "loading" | "not_ready" | "ready";

const FounderBubble = ({
  question,
  lang,
}: {
  readonly question: string;
  readonly lang?: DetectedLanguage;
}): React.JSX.Element => (
  <div className="msg-bubble me" lang={lang}>
    <span className="sr-only">You: </span>
    <span>{question}</span>
  </div>
);

const ThinkingTurn = (): React.JSX.Element => (
  <div className="msg-bubble crew" aria-busy="true" data-testid="thinking">
    <div className="msg-who">
      <img src={glimIllustration} alt="" width={16} height={16} aria-hidden="true" />
      Glim, your crew
    </div>
    <span className="thinking-dots" aria-hidden="true">
      <span />
      <span />
      <span />
    </span>
    <span className="sr-only">Glim is thinking…</span>
  </div>
);

const ExchangeTurn = ({
  exchange,
  isSolo,
  onRetry,
  onReask,
  onOpenFeatureLane,
  onAskAboutUpgrading,
}: {
  readonly exchange: Exchange;
  readonly isSolo: boolean;
  readonly onRetry: (exchange: Exchange) => void;
  readonly onReask: (exchange: Exchange, language: DetectedLanguage) => void;
  readonly onOpenFeatureLane: () => void;
  readonly onAskAboutUpgrading: () => void;
}): React.JSX.Element => {
  const [wrongLanguageOpen, setWrongLanguageOpen] = useState(false);

  if (exchange.status === "pending") {
    return (
      <>
        <FounderBubble question={exchange.question} />
        <ThinkingTurn />
      </>
    );
  }
  if (exchange.status === "stalled") {
    return (
      <>
        <FounderBubble question={exchange.question} />
        <div className="msg-bubble crew" role="alert" data-testid="stalled">
          <p style={{ margin: 0 }}>That&apos;s taking longer than expected. Try again?</p>
          <button
            type="button"
            className="btn btn-ghost btn-sm mt8"
            onClick={() => {
              onRetry(exchange);
            }}
          >
            Retry
          </button>
        </div>
      </>
    );
  }
  if (exchange.status === "error") {
    return (
      <>
        <FounderBubble question={exchange.question} />
        <div className="msg-bubble crew" role="alert" data-testid="error">
          <p style={{ margin: 0 }}>That didn&apos;t go through. Try again?</p>
          <button
            type="button"
            className="btn btn-ghost btn-sm mt8"
            onClick={() => {
              onRetry(exchange);
            }}
          >
            Retry
          </button>
        </div>
      </>
    );
  }
  if (exchange.status === "throttled") {
    return (
      <>
        <FounderBubble question={exchange.question} />
        <div className="msg-bubble crew" role="alert" data-testid="throttled">
          <p style={{ margin: 0 }}>Slow down a little - try again in a moment.</p>
        </div>
      </>
    );
  }

  return (
    <>
      <FounderBubble question={exchange.question} lang={exchange.answer.language} />
      <CrewAnswer
        answer={exchange.answer}
        isSolo={isSolo}
        onOpenFeatureLane={onOpenFeatureLane}
        onAskAboutUpgrading={onAskAboutUpgrading}
      />
      <div>
        {/* W8 fix (design-review): demoted from .btn.btn-ghost.btn-sm (full button
            chrome, repeated under every turn - clutter over a long thread) to a
            quiet text-link treatment (.turn-lang-toggle). Still a real <button>, so
            it stays keyboard/AT reachable and keeps its 44px tap target via padding
            rather than visual size. */}
        <button
          type="button"
          className="turn-lang-toggle"
          aria-expanded={wrongLanguageOpen}
          onClick={() => {
            setWrongLanguageOpen((open) => !open);
          }}
        >
          Wrong language?
        </button>
        {wrongLanguageOpen ? (
          <div className="row wrap-g gap8 mt8" role="group" aria-label="Answer in a different language">
            {LANGUAGE_CODES.map((code) => (
              <button
                key={code}
                type="button"
                className="chip"
                onClick={() => {
                  onReask(exchange, code);
                  setWrongLanguageOpen(false);
                }}
              >
                {`Answer in ${code.toUpperCase()} instead`}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </>
  );
};

export const AskCrew = (): React.JSX.Element => {
  const { appId, app } = useOutletContext<AppDetailContext>();
  // VERIFIED GAP (see CrewAnswer.tsx's `isSolo` doc comment): `Account.planId` is a bare
  // UUID, not a tier name - the client cannot resolve Solo/Studio/Business today. `false`
  // is a placeholder default (shows the Studio/Business CTA) until the tier name is
  // available on the session response - flagged in the dispatch return, not a silent
  // decision.
  const isSolo = false;

  const [readiness, setReadiness] = useState<Readiness>("loading");
  const [thread, setThread] = useState<readonly Exchange[]>([]);
  const [composerText, setComposerText] = useState("");
  const settledRef = useRef<Set<string>>(new Set());
  // Fix-round item 2/3 (§5.5, §2.9): a real DOM ref to the composer's <textarea> so
  // focus can be moved to it programmatically - both from the "Skip to composer"
  // link (which needs deterministic, cross-browser focus movement rather than
  // relying on native fragment-navigation focus, which browsers apply
  // inconsistently) and after an async send/retry clears or re-triggers the
  // composer (a click on "Ask" leaves focus on the Ask button, which then
  // disables once the composer empties - without this, focus falls through to
  // document.body).
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const composerId = useId();
  const threadId = useId();

  useEffect(() => {
    const cancelled = { current: false };
    void (async () => {
      const result = await getKnowledgeDoc(appId);
      if (cancelled.current) return;
      if (isErr(result)) {
        const kind = knowledgeErrorKind(result.error);
        // "building" is the confirmed still-indexing wire signal (Wave-3 seam
        // reconciliation); not_found after the AppDetail gate means never-queued.
        // audit_failed leaves the chat READY - the crew answers by abstaining.
        setReadiness(kind === "not_found" || kind === "building" ? "not_ready" : "ready");
        return;
      }
      const { features, dataMap, dependencies, rankedRisks } = result.value;
      const allEmpty =
        features.length === 0 &&
        dataMap.length === 0 &&
        dependencies.length === 0 &&
        rankedRisks.length === 0;
      setReadiness(allEmpty ? "not_ready" : "ready");
    })();
    return () => {
      cancelled.current = true;
    };
  }, [appId]);

  const runExchange = (id: string, question: string, preferredLanguage?: DetectedLanguage): void => {
    settledRef.current.delete(id);
    const timeoutId = setTimeout(() => {
      if (settledRef.current.has(id)) return;
      settledRef.current.add(id);
      setThread((t) =>
        t.map((ex) => (ex.id === id && ex.status === "pending" ? { ...ex, status: "stalled" } : ex)),
      );
    }, STALL_TIMEOUT_MS);

    void (async () => {
      const result = await askCrew(appId, { question, preferredLanguage: preferredLanguage ?? null });
      if (settledRef.current.has(id)) return; // already stalled - the timeout already resolved
      settledRef.current.add(id);
      clearTimeout(timeoutId);
      if (isErr(result)) {
        const nextStatus: Exchange["status"] = isThrottled(result.error) ? "throttled" : "error";
        setThread((t) => t.map((ex) => (ex.id === id ? { status: nextStatus, id, question } : ex)));
        return;
      }
      setThread((t) =>
        t.map((ex) => (ex.id === id ? { status: "answered", id, question, answer: result.value } : ex)),
      );
    })();
  };

  // Shared by `send` and `handleRetry` so a retry never reuses a stale id (fix-round
  // item 4 below).
  const makeExchangeId = (): string => `${String(Date.now())}-${Math.random().toString(36).slice(2)}`;

  const send = (question: string, preferredLanguage?: DetectedLanguage): void => {
    const trimmed = question.trim();
    if (trimmed.length === 0 || trimmed.length > HARD_LIMIT) return;
    const id = makeExchangeId();
    setThread((t) => [...t, { status: "pending", id, question: trimmed }]);
    runExchange(id, trimmed, preferredLanguage);
  };

  const handleSend = (): void => {
    send(composerText);
    setComposerText("");
    // §5.5 "composer clears, keeps focus": a click on "Ask" leaves focus on the
    // button, which disables the instant the composer empties (composerText === "")
    // - the browser then blurs to document.body with nothing to reclaim it. Move
    // focus back into the composer explicitly rather than relying on the disabled
    // button to somehow retain it.
    composerRef.current?.focus();
  };

  const handleRetry = (exchange: Exchange): void => {
    // Fix-round item 4 (stale-answer race): a retry must NOT reuse the original
    // exchange's id. The original call is still in flight in the background (that
    // is exactly why it stalled/errored) - if the retry kept the same id,
    // `runExchange`'s `settledRef.current.delete(id)` re-opens that id, and
    // whichever of the two in-flight calls (the stale original or the fresh
    // retry) resolves first wins the `settledRef` guard and writes the thread
    // slot, discarding the other - including a real retry answer discarded by a
    // late stale one. Minting a FRESH id for the retry means the thread slot the
    // stale original targets no longer exists (the `t.map` in `runExchange` finds
    // no match), and explicitly marking the OLD id settled means even a still-
    // pending timeout tied to that id treats it as already resolved.
    settledRef.current.add(exchange.id);
    const freshId = makeExchangeId();
    setThread((t) =>
      t.map((ex) =>
        ex.id === exchange.id ? { status: "pending", id: freshId, question: ex.question } : ex,
      ),
    );
    runExchange(freshId, exchange.question);
    composerRef.current?.focus();
  };

  const handleReask = (exchange: Exchange, language: DetectedLanguage): void => {
    send(exchange.question, language);
  };

  const remaining = HARD_LIMIT - composerText.length;
  const showCounter = composerText.length >= SOFT_LIMIT_WARN_AT;
  // Fix-round item 6: the visible counter above still updates every keystroke
  // (unchanged), but the announced copy is bucketed to 100-character steps, rounded
  // UP (ceiling), so an `aria-live="polite"` region does not re-announce on every
  // single keystroke past the 3500-character mark. Ceiling (not floor) matters here:
  // it keeps the announced bucket ("About 500 characters left") stable for the
  // whole 100-character span BELOW the threshold that first showed it, instead of
  // dropping to the next bucket on the very first keystroke past the boundary.
  const announceStep = remaining > 0 ? Math.ceil(remaining / 100) * 100 : 0;

  if (readiness === "loading") {
    return (
      <div className="wrap">
        <div className="skeleton skeleton-line" style={{ width: "30%" }} />
        <div className="skeleton skeleton-line mt8" style={{ width: "20%", height: 24 }} />
        <div className="card skeleton-card mt16" />
      </div>
    );
  }

  return (
    <div className="wrap">
      {/* C3 fix (design-review): promoted from .h3 (17px, the same visual weight as
          the topbar's own presentational .title) to .h2 (23px, Fraunces), restoring
          a real step above the section headings in the right rail, which stay
          .h3. */}
      <h1 className="h2">Ask your crew - {app.name}</h1>
      <span className="chip mt8" style={{ display: "inline-flex" }}>
        <span className="win sm" aria-hidden="true" /> This app only
      </span>

      <a
        href={`#${composerId}`}
        className="skip-link"
        onClick={(event) => {
          // Point the skip link at the composer's actual <textarea> (fix-round item
          // 3) and move focus to it directly rather than relying on the browser's
          // native hash-fragment focus behavior, which is inconsistent across
          // browsers and does nothing in the jsdom test environment.
          event.preventDefault();
          composerRef.current?.focus();
        }}
      >
        Skip to composer
      </a>

      <div className="ask-layout mt16">
        <div className="card pad">
          {readiness === "not_ready" ? (
            <div className="banner amber" role="status">
              <span className="grow">
                {app.name}&apos;s knowledge is still building. Answers may be limited until
                tonight&apos;s shift finishes indexing - ask anyway, or check back later.
              </span>
            </div>
          ) : null}

          {/* W10 fix (design-review): "mb16" (the banner's former class) is undefined
              anywhere in app.css/brand.css - a dead class, no spacing landed. Reuses
              the existing .mt16 utility (brand.css) on the following sibling instead
              of inventing a new margin-bottom class, applied only when the banner
              actually precedes the thread. */}
          <div
            className={`chat-thread${readiness === "not_ready" ? " mt16" : ""}`}
            role="log"
            aria-label="Conversation with your crew"
            id={threadId}
          >
            {thread.length === 0 ? (
              <p className="muted">Ask me anything about {app.name}.</p>
            ) : (
              thread.map((exchange) => (
                <ExchangeTurn
                  key={exchange.id}
                  exchange={exchange}
                  isSolo={isSolo}
                  onRetry={handleRetry}
                  onReask={handleReask}
                  onOpenFeatureLane={() => undefined}
                  onAskAboutUpgrading={() => undefined}
                />
              ))
            )}
          </div>

          <div className="composer-row">
            <div className="field-wrap">
              <Field
                id={composerId}
                inputRef={composerRef}
                label={`Ask a question about ${app.name}`}
                hideLabel
                multiline
                rows={2}
                value={composerText}
                placeholder={`Ask anything about ${app.name}...`}
                onChange={setComposerText}
              />
              {showCounter ? (
                <p className="composer-count">
                  <span aria-hidden="true">{remaining} characters left</span>
                  <span className="sr-only" aria-live="polite">
                    {announceStep > 0
                      ? `About ${String(announceStep)} characters left`
                      : "Approaching the character limit"}
                  </span>
                </p>
              ) : null}
            </div>
            <button
              type="button"
              className="btn btn-primary"
              disabled={composerText.trim().length === 0 || composerText.length > HARD_LIMIT}
              onClick={handleSend}
            >
              Ask
            </button>
          </div>
        </div>

        <div className="stack gap16 ask-rail">
          {thread.length === 0 ? (
            <div className="card pad">
              <div className="eyebrow">Try asking</div>
              <div className="row wrap-g gap8 mt12">
                {SUGGESTED_QUESTIONS.map((question) => (
                  <button
                    key={question}
                    type="button"
                    className="qchip"
                    onClick={() => {
                      setComposerText(question);
                    }}
                  >
                    {question}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
          <div className="card pad">
            <div className="row aic gap8">
              <span className="win sm" aria-hidden="true" />
              <div className="h3">Grounded in your app</div>
            </div>
            <p className="muted mt8" style={{ fontSize: 13 }}>
              Answers read from the knowledge doc for {app.name}. No generic advice, and the crew
              says plainly what it knows versus what it assumes.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
